import { InputWithSuggestion } from '@components/forms/InputWithSuggestion';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@components/ui/accordion';
import { Button } from '@components/ui/button';
import { DatePicker } from '@components/ui/date-picker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@components/ui/dialog';
import { DispositionFieldIcon, SectionScoreBadge } from '@components/ui/disposition-status';
import { ExtractedValueDisplay, shouldShowExtractedValue } from '@components/ui/editable-field';
import { FieldEscalateControl } from '@components/ui/field-escalation';
import { HeardSeekButton } from '@components/ui/heard-seek-button';
import { Input } from '@components/ui/input';
import { MultiSelect } from '@components/ui/multi-select';
import { ReasoningTooltip, ReviewButton } from '@components/ui/review-button';
import { SearchableSelect } from '@components/ui/searchable-select';
import { UNKNOWN_DATE_VALUE } from '@utils/date';
import { formatComplexValue } from '@utils/formatValue';
import {
  buildFieldPath,
  createEmptyItem,
  getArrayItemBodyFields,
  getArrayItemLabel,
  getNestedValue,
  separateFieldTypes,
} from '@utils/nestedAccordion';
import { format } from 'date-fns';
import { ChevronsDownUp, ChevronsUpDown, Plus, Trash2 } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import ReactPhoneInput from 'react-phone-input-2';

import type { FieldEvidence, HeardSeek } from '@/components/fields/types';
import {
  type CloneableItem,
  CloneControls,
  useCloneItems,
  type UseCloneItemsResult,
} from '@/components/fields/useCloneItems';
import { nonNegativeInputProps } from '@/components/numericInput';
import type { GetFieldSuggestionProps } from '@/hooks/useCaseInputStorage';
import type { FormField } from '@/types/formSchema';
import { computeFieldScore, type DispositionFieldStatus } from '@/utils/dispositionCompleteness';
import { getCallSourceId } from '@/utils/evidence';

/**
 * Convert a dot-path with numeric segments into the bracket form used by the
 * disposition engine.
 *   "drug.0.coverage" → "drug[0].coverage"
 */
const toBracketPath = (dotPath: string): string => dotPath.replace(/\.(\d+)(?=\.|$)/g, '[$1]');

export interface NestedFieldAccordionProps {
  /** The field definition (may have nested fields) */
  field: FormField;
  /** The actual data for this field */
  data: any;
  /** Dot-notation path (e.g., "drug.0.coverage") */
  path: string;
  /** Current nesting depth */
  depth: number;
  /** Whether fields are editable */
  isEditable?: boolean;
  /** Callback when data changes */
  onDataChange?: (path: string, value: any) => void;
  /**
   * Callback when a field commits (text on blur; select/date/multi-select on
   * change). Used by the disposition assist panel on the case details page to
   * snapshot output data only at commit boundaries.
   */
  onFieldBlur?: (path: string) => void;
  /** Field errors keyed by path */
  fieldError?: Record<string, string>;
  /** Custom class names */
  className?: {
    container?: string;
    label?: string;
    value?: string;
  };
  /** Externally controlled expanded items (optional - uses internal state if not provided) */
  expandedItems?: string[];
  /** Callback when expanded items change (for controlled mode) */
  onExpandedItemsChange?: (items: string[]) => void;
  /**
   * Disposition required/missing fields (engine-side bracket paths). When
   * provided, nested subsection triggers render an "X/Y captured" badge.
   */
  dispositionFields?: { required_fields?: string[]; missing_fields?: string[] };
  /**
   * Per-field suggestion props, keyed by the field's full dot-path (e.g.
   * "drug.0.coverage.coverage_status"). When provided, editable scalar leaves
   * render the AI suggestion chip (used for human-call output editing).
   */
  getFieldSuggestionProps?: GetFieldSuggestionProps;
  /** Output metadata subtree aligned with `data` at this node (carries per-field heardSeek). */
  outputMetaNode?: Record<string, unknown>;
  /**
   * AI-extraction subtrees aligned with `data` at this node, mirroring
   * `outputMetaNode`'s indexing. Flat fields get these from `extractedData` /
   * `extractedMetadata` in FieldGroupAccordion; without them a nested leaf could
   * render neither the suggested value nor its reasoning, which is why drug and
   * procedure details showed no AI annotations while flat fields did.
   */
  extractedDataNode?: Record<string, unknown>;
  extractedMetaNode?: Record<string, unknown>;
  /** Accept-suggestion handler; the Sparkles affordance only appears during human audit. */
  onAcceptExtracted?: (fieldPath: string, value: any, fieldLabel: string) => void;
  isHumanAudit?: boolean;
  /** Restricts the suggestion chip to extractions from the call being viewed. */
  currentCallId?: string;
  onNavigateToCall?: (callId: string, evidenceTimeStart?: string, evidenceTimeEnd?: string) => void;
  onNavigateToAudit?: (auditId: string) => void;
}

// Depth-based indentation classes
const DEPTH_INDENT = ['pl-0', 'pl-2', 'pl-4', 'pl-6', 'pl-8'];

// Entity arrays that support item-to-item cloning (see useCloneItems / BI-1625).
// MEDBHOBV (BV-2328) carries a medbv_ and a bho_ copy of each entity array, so the
// prefixed ids are listed too — otherwise its multi-code cases offer no clone (BV-2662).
const CLONE_ENABLED_FIELDS = new Set([
  'drug',
  'procedure',
  'medbv_drug',
  'bho_drug',
  'medbv_procedure',
  'bho_procedure',
]);

/**
 * NestedFieldAccordion - Recursive component for rendering nested schema fields
 *
 * Handles:
 * - Array fields: Renders each item as a collapsible accordion
 * - Object fields: Renders as a collapsible subsection
 * - Scalar fields: Renders as regular form fields
 */
export const NestedFieldAccordion: React.FC<NestedFieldAccordionProps> = ({
  field,
  data,
  path,
  depth,
  isEditable = false,
  onDataChange,
  onFieldBlur,
  fieldError = {},
  className = {},
  expandedItems: externalExpandedItems,
  onExpandedItemsChange: externalOnExpandedItemsChange,
  dispositionFields,
  getFieldSuggestionProps,
  outputMetaNode,
  extractedDataNode,
  extractedMetaNode,
  onAcceptExtracted,
  isHumanAudit,
  currentCallId,
  onNavigateToAudit,
  onNavigateToCall,
}) => {
  // Use internal state only if not externally controlled
  const [internalExpandedItems, setInternalExpandedItems] = useState<string[]>([]);

  const expandedItems = externalExpandedItems ?? internalExpandedItems;
  const setExpandedItems = externalOnExpandedItemsChange ?? setInternalExpandedItems;

  const indent = DEPTH_INDENT[Math.min(depth, DEPTH_INDENT.length - 1)];

  if (field.type === 'array') {
    return (
      <ArrayFieldRenderer
        field={field}
        data={data}
        path={path}
        depth={depth}
        isEditable={isEditable}
        onDataChange={onDataChange}
        onFieldBlur={onFieldBlur}
        fieldError={fieldError}
        className={className}
        expandedItems={expandedItems}
        onExpandedItemsChange={setExpandedItems}
        indent={indent}
        dispositionFields={dispositionFields}
        getFieldSuggestionProps={getFieldSuggestionProps}
        outputMetaNode={outputMetaNode}
        extractedDataNode={extractedDataNode}
        extractedMetaNode={extractedMetaNode}
        onAcceptExtracted={onAcceptExtracted}
        isHumanAudit={isHumanAudit}
        currentCallId={currentCallId}
        onNavigateToAudit={onNavigateToAudit}
        onNavigateToCall={onNavigateToCall}
      />
    );
  }

  if (field.type === 'object') {
    return (
      <ObjectFieldRenderer
        field={field}
        data={data}
        path={path}
        depth={depth}
        isEditable={isEditable}
        onDataChange={onDataChange}
        onFieldBlur={onFieldBlur}
        fieldError={fieldError}
        className={className}
        indent={indent}
        dispositionFields={dispositionFields}
        getFieldSuggestionProps={getFieldSuggestionProps}
        outputMetaNode={outputMetaNode}
        extractedDataNode={extractedDataNode}
        extractedMetaNode={extractedMetaNode}
        onAcceptExtracted={onAcceptExtracted}
        isHumanAudit={isHumanAudit}
        currentCallId={currentCallId}
        onNavigateToAudit={onNavigateToAudit}
        onNavigateToCall={onNavigateToCall}
      />
    );
  }

  // Scalar field - should not reach here normally
  return null;
};

interface ArrayFieldRendererProps extends NestedFieldAccordionProps {
  expandedItems: string[];
  onExpandedItemsChange: (items: string[]) => void;
  indent: string;
  outputMetaNode?: Record<string, unknown>;
  extractedDataNode?: Record<string, unknown>;
  extractedMetaNode?: Record<string, unknown>;
  onAcceptExtracted?: (fieldPath: string, value: any, fieldLabel: string) => void;
  isHumanAudit?: boolean;
  currentCallId?: string;
  onNavigateToAudit?: (auditId: string) => void;
  onNavigateToCall?: (callId: string, evidenceTimeStart?: string, evidenceTimeEnd?: string) => void;
}

/**
 * Renders an array field with each item as a collapsible accordion
 */
const ArrayFieldRenderer: React.FC<ArrayFieldRendererProps> = ({
  field,
  data,
  path,
  depth,
  isEditable,
  onDataChange,
  onFieldBlur,
  fieldError,
  className,
  expandedItems,
  onExpandedItemsChange,
  indent,
  dispositionFields,
  getFieldSuggestionProps,
  outputMetaNode,
  extractedDataNode,
  extractedMetaNode,
  onAcceptExtracted,
  isHumanAudit,
  currentCallId,
  onNavigateToAudit,
  onNavigateToCall,
}) => {
  const items = Array.isArray(data) ? data : [];
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState<number | null>(null);

  // Item-to-item clone (BI-1625). Reuses the same write path as add/delete
  // (onDataChange(path, newItems)). Enabled only for entity arrays, when
  // editable and there is more than one item to clone between.
  const clone = useCloneItems(items as CloneableItem[], newItems => onDataChange?.(path, newItems));
  const cloneEnabled =
    Boolean(isEditable) && CLONE_ENABLED_FIELDS.has(field.id) && items.length > 1;
  const itemClone: UseCloneItemsResult | null = cloneEnabled ? clone : null;

  const handleAddItem = () => {
    const newItem = createEmptyItem(field);
    const newItems = [...items, newItem];
    onDataChange?.(path, newItems);
    // Auto-expand the newly added item
    const newItemPath = buildFieldPath(path, String(newItems.length - 1));
    onExpandedItemsChange([...expandedItems, newItemPath]);
  };

  const handleDeleteItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    onDataChange?.(path, newItems);
    setDeleteConfirmIndex(null);
  };

  const itemLabel = field.item_label || field.label || 'Item';

  // Expand/collapse all item accordions in this array at once. Only this array's
  // item paths are touched, so sibling arrays' expand state is preserved.
  const allItemPaths = items.map((_, i) => buildFieldPath(path, String(i)));
  const allExpanded = allItemPaths.length > 0 && allItemPaths.every(p => expandedItems.includes(p));
  const toggleExpandAll = () => {
    if (allExpanded) {
      onExpandedItemsChange(expandedItems.filter(p => !allItemPaths.includes(p)));
    } else {
      onExpandedItemsChange([...new Set([...expandedItems, ...allItemPaths])]);
    }
  };

  if (items.length === 0) {
    return (
      <div className={`${indent} py-2`}>
        <div className="text-sm text-muted-foreground italic mb-2">
          No {field.label?.toLowerCase() || 'items'}
        </div>
        {isEditable && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddItem}
            className="h-7 text-xs"
          >
            <Plus className="w-3 h-3 mr-1" />
            Add {itemLabel}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={indent}>
      {items.length > 1 && (
        <div className="flex justify-end mb-1">
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={toggleExpandAll}
            className="h-7 w-7 p-0 text-muted-foreground"
            title={allExpanded ? 'Collapse all' : 'Expand all'}
          >
            {allExpanded ? (
              <ChevronsDownUp className="w-4 h-4" />
            ) : (
              <ChevronsUpDown className="w-4 h-4" />
            )}
          </Button>
        </div>
      )}
      <Accordion
        type="multiple"
        value={expandedItems}
        onValueChange={onExpandedItemsChange}
        className="space-y-1"
      >
        {items.map((item, index) => {
          const itemPath = buildFieldPath(path, String(index));
          const displayLabel = getArrayItemLabel(item, field, index);

          return (
            <ArrayItemAccordion
              key={itemPath}
              item={item}
              field={field}
              path={itemPath}
              depth={depth}
              isEditable={isEditable}
              onDataChange={onDataChange}
              onFieldBlur={onFieldBlur}
              fieldError={fieldError}
              className={className}
              itemLabel={displayLabel}
              onDeleteClick={() => setDeleteConfirmIndex(index)}
              clone={itemClone}
              itemIndex={index}
              entityLabel={field.item_label || field.label || 'item'}
              dispositionFields={dispositionFields}
              getFieldSuggestionProps={getFieldSuggestionProps}
              outputMetaNode={
                Array.isArray(outputMetaNode)
                  ? (outputMetaNode[index] as Record<string, unknown>)
                  : undefined
              }
              extractedDataNode={
                Array.isArray(extractedDataNode)
                  ? (extractedDataNode[index] as Record<string, unknown>)
                  : undefined
              }
              extractedMetaNode={
                Array.isArray(extractedMetaNode)
                  ? (extractedMetaNode[index] as Record<string, unknown>)
                  : undefined
              }
              onAcceptExtracted={onAcceptExtracted}
              isHumanAudit={isHumanAudit}
              currentCallId={currentCallId}
              onNavigateToAudit={onNavigateToAudit}
              onNavigateToCall={onNavigateToCall}
            />
          );
        })}
      </Accordion>

      {/* Add button after items */}
      {isEditable && (
        <div className="mt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddItem}
            className="h-7 text-xs"
          >
            <Plus className="w-3 h-3 mr-1" />
            Add {itemLabel}
          </Button>
        </div>
      )}

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteConfirmIndex !== null}
        onOpenChange={(open: boolean) => !open && setDeleteConfirmIndex(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {itemLabel}?</DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete this{' '}
              {itemLabel.toLowerCase()} and all its data.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmIndex(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteConfirmIndex !== null && handleDeleteItem(deleteConfirmIndex)}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

interface ArrayItemAccordionProps {
  item: any;
  field: FormField;
  path: string;
  depth: number;
  isEditable?: boolean;
  onDataChange?: (path: string, value: any) => void;
  onFieldBlur?: (path: string) => void;
  fieldError?: Record<string, string>;
  className?: {
    container?: string;
    label?: string;
    value?: string;
  };
  itemLabel: string;
  onDeleteClick?: () => void;
  /** Clone-mode controller (null when cloning is disabled for this array). */
  clone?: UseCloneItemsResult | null;
  /** This item's index within the array (for clone selection). */
  itemIndex?: number;
  /** Entity noun for clone control labels/tooltips (e.g. "Drug"). */
  entityLabel?: string;
  dispositionFields?: { required_fields?: string[]; missing_fields?: string[] };
  getFieldSuggestionProps?: GetFieldSuggestionProps;
  outputMetaNode?: Record<string, unknown>;
  extractedDataNode?: Record<string, unknown>;
  extractedMetaNode?: Record<string, unknown>;
  onAcceptExtracted?: (fieldPath: string, value: any, fieldLabel: string) => void;
  isHumanAudit?: boolean;
  currentCallId?: string;
  onNavigateToAudit?: (auditId: string) => void;
  onNavigateToCall?: (callId: string, evidenceTimeStart?: string, evidenceTimeEnd?: string) => void;
}

/**
 * Renders an individual array item with its nested fields
 */
const ArrayItemAccordion: React.FC<ArrayItemAccordionProps> = ({
  item,
  field,
  path,
  depth,
  isEditable,
  onDataChange,
  onFieldBlur,
  fieldError,
  className,
  itemLabel,
  onDeleteClick,
  clone,
  itemIndex,
  entityLabel,
  dispositionFields,
  getFieldSuggestionProps,
  outputMetaNode,
  extractedDataNode,
  extractedMetaNode,
  onAcceptExtracted,
  isHumanAudit,
  currentCallId,
  onNavigateToAudit,
  onNavigateToCall,
}) => {
  const [expandedSubsections, setExpandedSubsections] = useState<string[]>([]);

  // In read mode the schema-declared header field is omitted here — `itemLabel`
  // above already displays it, so rendering it as a row too would show it twice.
  const { scalarFields, nestedFields } = useMemo(
    () => separateFieldTypes(getArrayItemBodyFields(item, field, isEditable)),
    [item, field, isEditable],
  );

  // Aggregate captured/required across the whole item (e.g. drug[0] rolls up
  // drug[0].coverage.*, drug[0].prior_authorization.*, …), mirroring the
  // subsection badges below.
  const itemScore = computeFieldScore(
    toBracketPath(path),
    dispositionFields?.required_fields,
    dispositionFields?.missing_fields,
  );

  return (
    <AccordionItem
      value={path}
      data-accordion-path={path}
      className="border rounded-md bg-muted/30"
    >
      <AccordionTrigger
        className="px-3 py-2 text-sm font-medium hover:bg-muted/50 rounded-md data-[state=open]:rounded-b-none"
        action={
          <div className="flex items-center gap-1 mr-2">
            {clone && itemIndex !== undefined && (
              <CloneControls index={itemIndex} clone={clone} entityLabel={entityLabel} />
            )}
            {isEditable && onDeleteClick && !clone?.active ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={e => {
                  e.stopPropagation();
                  onDeleteClick();
                }}
                className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            ) : null}
          </div>
        }
      >
        <div className="flex items-center gap-2 flex-1">
          <span>{itemLabel}</span>
          <SectionScoreBadge score={itemScore} />
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-3 pb-3">
        {/* Render scalar fields first */}
        {scalarFields.length > 0 && (
          <div className="space-y-2 mb-3">
            {scalarFields.map(subField => (
              <ScalarFieldRenderer
                key={subField.id}
                field={subField}
                data={item}
                basePath={path}
                isEditable={isEditable}
                onDataChange={onDataChange}
                onFieldBlur={onFieldBlur}
                fieldError={fieldError}
                className={className}
                dispositionFields={dispositionFields}
                getFieldSuggestionProps={getFieldSuggestionProps}
                outputMetaNode={outputMetaNode}
                extractedDataNode={extractedDataNode}
                extractedMetaNode={extractedMetaNode}
                onAcceptExtracted={onAcceptExtracted}
                isHumanAudit={isHumanAudit}
                currentCallId={currentCallId}
                onNavigateToAudit={onNavigateToAudit}
                onNavigateToCall={onNavigateToCall}
              />
            ))}
          </div>
        )}

        {/* Render nested (object/array) fields as sub-accordions */}
        {nestedFields.length > 0 && (
          <Accordion
            type="multiple"
            value={expandedSubsections}
            onValueChange={setExpandedSubsections}
            className="space-y-1"
          >
            {nestedFields.map(subField => {
              const subPath = buildFieldPath(path, subField.id);
              const subData = item?.[subField.id];
              const subScore = computeFieldScore(
                toBracketPath(subPath),
                dispositionFields?.required_fields,
                dispositionFields?.missing_fields,
              );

              return (
                <AccordionItem
                  key={subField.id}
                  value={subPath}
                  data-accordion-path={subPath}
                  className="border rounded-md"
                >
                  <AccordionTrigger className="px-3 py-2 text-sm font-medium hover:bg-muted/50 rounded-md data-[state=open]:rounded-b-none">
                    <div className="flex items-center gap-2 flex-1">
                      <span>{subField.label}</span>
                      <SectionScoreBadge score={subScore} />
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-3 pb-3">
                    <NestedFieldAccordion
                      field={subField}
                      data={subData}
                      path={subPath}
                      depth={depth + 1}
                      isEditable={isEditable}
                      onDataChange={onDataChange}
                      onFieldBlur={onFieldBlur}
                      fieldError={fieldError}
                      className={className}
                      dispositionFields={dispositionFields}
                      getFieldSuggestionProps={getFieldSuggestionProps}
                      outputMetaNode={
                        outputMetaNode?.[subField.id] as Record<string, unknown> | undefined
                      }
                      extractedDataNode={
                        extractedDataNode?.[subField.id] as Record<string, unknown> | undefined
                      }
                      extractedMetaNode={
                        extractedMetaNode?.[subField.id] as Record<string, unknown> | undefined
                      }
                      onAcceptExtracted={onAcceptExtracted}
                      isHumanAudit={isHumanAudit}
                      currentCallId={currentCallId}
                      onNavigateToAudit={onNavigateToAudit}
                      onNavigateToCall={onNavigateToCall}
                    />
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        )}
      </AccordionContent>
    </AccordionItem>
  );
};

interface ObjectFieldRendererProps extends NestedFieldAccordionProps {
  indent: string;
  outputMetaNode?: Record<string, unknown>;
  extractedDataNode?: Record<string, unknown>;
  extractedMetaNode?: Record<string, unknown>;
  onAcceptExtracted?: (fieldPath: string, value: any, fieldLabel: string) => void;
  isHumanAudit?: boolean;
  currentCallId?: string;
  onNavigateToAudit?: (auditId: string) => void;
  onNavigateToCall?: (callId: string, evidenceTimeStart?: string, evidenceTimeEnd?: string) => void;
}

/**
 * Renders an object field with its nested fields
 */
const ObjectFieldRenderer: React.FC<ObjectFieldRendererProps> = ({
  field,
  data,
  path,
  depth,
  isEditable,
  onDataChange,
  onFieldBlur,
  fieldError,
  className,
  indent,
  dispositionFields,
  getFieldSuggestionProps,
  outputMetaNode,
  extractedDataNode,
  extractedMetaNode,
  onAcceptExtracted,
  isHumanAudit,
  currentCallId,
  onNavigateToAudit,
  onNavigateToCall,
}) => {
  const obj = data || {};
  const { scalarFields, nestedFields } = useMemo(
    () => separateFieldTypes(field.fields || []),
    [field.fields],
  );

  const [expandedSubsections, setExpandedSubsections] = useState<string[]>([]);

  return (
    <div className={indent}>
      {/* Render scalar fields */}
      {scalarFields.length > 0 && (
        <div className="space-y-2 mb-3">
          {scalarFields.map(subField => (
            <ScalarFieldRenderer
              key={subField.id}
              field={subField}
              data={obj}
              basePath={path}
              isEditable={isEditable}
              onDataChange={onDataChange}
              onFieldBlur={onFieldBlur}
              fieldError={fieldError}
              className={className}
              dispositionFields={dispositionFields}
              getFieldSuggestionProps={getFieldSuggestionProps}
              outputMetaNode={outputMetaNode}
              extractedDataNode={extractedDataNode}
              extractedMetaNode={extractedMetaNode}
              onAcceptExtracted={onAcceptExtracted}
              isHumanAudit={isHumanAudit}
              currentCallId={currentCallId}
              onNavigateToAudit={onNavigateToAudit}
              onNavigateToCall={onNavigateToCall}
            />
          ))}
        </div>
      )}

      {/* Render nested fields as sub-accordions */}
      {nestedFields.length > 0 && (
        <Accordion
          type="multiple"
          value={expandedSubsections}
          onValueChange={setExpandedSubsections}
          className="space-y-1"
        >
          {nestedFields.map(subField => {
            const subPath = buildFieldPath(path, subField.id);
            const subData = obj?.[subField.id];
            const subScore = computeFieldScore(
              toBracketPath(subPath),
              dispositionFields?.required_fields,
              dispositionFields?.missing_fields,
            );

            return (
              <AccordionItem
                key={subField.id}
                value={subPath}
                data-accordion-path={subPath}
                className="border rounded-md"
              >
                <AccordionTrigger className="px-3 py-2 text-sm font-medium hover:bg-muted/50 rounded-md data-[state=open]:rounded-b-none">
                  <div className="flex items-center gap-2 flex-1">
                    <span>{subField.label}</span>
                    <SectionScoreBadge score={subScore} />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-3 pb-3">
                  <NestedFieldAccordion
                    field={subField}
                    data={subData}
                    path={subPath}
                    depth={depth + 1}
                    isEditable={isEditable}
                    onDataChange={onDataChange}
                    onFieldBlur={onFieldBlur}
                    fieldError={fieldError}
                    className={className}
                    dispositionFields={dispositionFields}
                    outputMetaNode={
                      outputMetaNode?.[subField.id] as Record<string, unknown> | undefined
                    }
                    extractedDataNode={
                      extractedDataNode?.[subField.id] as Record<string, unknown> | undefined
                    }
                    extractedMetaNode={
                      extractedMetaNode?.[subField.id] as Record<string, unknown> | undefined
                    }
                    onAcceptExtracted={onAcceptExtracted}
                    isHumanAudit={isHumanAudit}
                    currentCallId={currentCallId}
                    onNavigateToAudit={onNavigateToAudit}
                    onNavigateToCall={onNavigateToCall}
                  />
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}
    </div>
  );
};

interface ScalarFieldRendererProps {
  field: FormField;
  data: any;
  basePath: string;
  isEditable?: boolean;
  onDataChange?: (path: string, value: any) => void;
  onFieldBlur?: (path: string) => void;
  fieldError?: Record<string, string>;
  className?: {
    container?: string;
    label?: string;
    value?: string;
  };
  dispositionFields?: { required_fields?: string[]; missing_fields?: string[] };
  getFieldSuggestionProps?: GetFieldSuggestionProps;
  outputMetaNode?: Record<string, unknown>;
  extractedDataNode?: Record<string, unknown>;
  extractedMetaNode?: Record<string, unknown>;
  onAcceptExtracted?: (fieldPath: string, value: any, fieldLabel: string) => void;
  isHumanAudit?: boolean;
  currentCallId?: string;
  onNavigateToAudit?: (auditId: string) => void;
  onNavigateToCall?: (callId: string, evidenceTimeStart?: string, evidenceTimeEnd?: string) => void;
}

/**
 * Renders a scalar (non-array, non-object) field
 */
const ScalarFieldRenderer: React.FC<ScalarFieldRendererProps> = ({
  field,
  data,
  basePath,
  isEditable,
  onDataChange,
  onFieldBlur,
  fieldError,
  className,
  dispositionFields,
  getFieldSuggestionProps,
  outputMetaNode,
  extractedDataNode,
  extractedMetaNode,
  onAcceptExtracted,
  isHumanAudit,
  currentCallId,
  onNavigateToAudit,
  onNavigateToCall,
}) => {
  const fieldPath = buildFieldPath(basePath, field.id);
  // Unique DOM id based on full path — prevents duplicate ids when the same
  // scalar field name (e.g. "code") appears in multiple nested arrays.
  const domId = fieldPath.replace(/\./g, '-');
  const value = getNestedValue(data, field.id, isEditable ? '' : '--');
  const error = fieldError?.[fieldPath];

  // Per-field disposition status (filled / missing / undefined). The engine
  // emits bracket-form paths; convert and look up against required/missing.
  const dispositionStatus: DispositionFieldStatus | undefined = (() => {
    const required = dispositionFields?.required_fields;
    if (!required || required.length === 0) return undefined;
    const bracket = toBracketPath(fieldPath);
    if (!required.includes(bracket)) return undefined;
    return dispositionFields?.missing_fields?.includes(bracket) ? 'missing' : 'filled';
  })();
  const showDispositionIcon = dispositionStatus !== undefined;

  const leafHeardSeek = (outputMetaNode?.[field.id] as { heardSeek?: HeardSeek } | undefined)
    ?.heardSeek;

  // AI-extraction annotations for this leaf, mirroring what EditableField renders for
  // a flat field: the suggested value (with accept, during human audit) plus the
  // reasoning/review affordances behind it. `extractedDataNode` / `extractedMetaNode`
  // are already indexed to this node by the callers above.
  const leafMeta = extractedMetaNode?.[field.id] as
    | { evidence?: FieldEvidence[]; source_id?: string; source_type?: 'call' | 'ai_audit' }
    | undefined;
  const leafOutputEvidence = (
    outputMetaNode?.[field.id] as { evidence?: FieldEvidence[] } | undefined
  )?.evidence;
  const extractedValue = extractedDataNode ? (extractedDataNode[field.id] ?? null) : undefined;
  const extractedSourceCallId = getCallSourceId(leafMeta);
  const showExtracted = shouldShowExtractedValue({
    extractedValue,
    value,
    field,
    currentCallId,
    sourceCallId: extractedSourceCallId,
  });
  // The label row's reasoning tooltip mirrors EditableField's `outputEvidence` branch.
  const outputReasoning = !!leafOutputEvidence?.length && onNavigateToCall && (
    <>
      <ReviewButton
        sourceCallId={getCallSourceId(outputMetaNode?.[field.id] as never)}
        evidence={leafOutputEvidence}
        onNavigateToCall={onNavigateToCall}
        onNavigateToAudit={onNavigateToAudit}
        className="text-xs"
      />
      <ReasoningTooltip evidence={leafOutputEvidence} />
    </>
  );
  const extractedAnnotation = showExtracted ? (
    <ExtractedValueDisplay
      extractedValue={extractedValue}
      field={field}
      fieldPath={fieldPath}
      label={field.label}
      // Nested rows have no hover state of their own; human audit shows the accept
      // affordance directly rather than hiding it behind a hover the row can't report.
      isHovered
      isHumanAudit={!!isHumanAudit}
      isEditingField={false}
      onAcceptExtracted={onAcceptExtracted}
      sourceCallId={extractedSourceCallId}
      evidence={leafMeta?.evidence}
      onNavigateToCall={onNavigateToCall}
      onNavigateToAudit={onNavigateToAudit}
    />
  ) : null;

  const handleChange = (newValue: any) => {
    onDataChange?.(fieldPath, newValue);
  };
  const handleBlur = () => {
    onFieldBlur?.(fieldPath);
  };

  // Format display value for read-only mode
  const formatDisplayValue = (val: any): string => {
    if (val === undefined || val === null || val === '') return '--';

    // Handle boolean values
    if (typeof val === 'boolean') {
      return val ? 'Yes' : 'No';
    }

    // Handle multi-select values (array of enum values)
    if (Array.isArray(val) && field.options) {
      const labels = val
        .map(v => field.options?.find(opt => opt.value === v)?.label || v)
        .filter(Boolean);
      return labels.length > 0 ? labels.join(', ') : '--';
    }

    // Handle select fields with options
    if (field.options) {
      const option = field.options.find(opt => opt.value === val);
      if (option) return option.label;
    }

    // Handle objects/arrays using shared utility
    if (typeof val === 'object' && val !== null) {
      return formatComplexValue(val);
    }
    return String(val);
  };

  if (!isEditable) {
    // Read-only display
    return (
      <div className={className?.container || 'px-2 py-1.5'} data-field-id={fieldPath}>
        {/* The Escalate icon shares the leaf's row (and narrows it by its width) — only
            under a FieldEscalationProvider (the Human Audit in-progress board in edit
            mode); elsewhere this passes the leaf through untouched. */}
        <FieldEscalateControl fieldPath={fieldPath} fieldLabel={field.label}>
          <div
            className={`${className?.label || 'text-xs text-muted-foreground mb-0.5'} flex items-center gap-1.5`}
          >
            {showDispositionIcon && <DispositionFieldIcon status={dispositionStatus} />}
            <span>{field.label}</span>
            {leafHeardSeek?.source_id && onNavigateToCall && (
              <HeardSeekButton heardSeek={leafHeardSeek} onNavigateToCall={onNavigateToCall} />
            )}
            {outputReasoning}
          </div>
          <div className={className?.value || 'text-sm font-medium'}>
            {formatDisplayValue(value)}
          </div>
          {extractedAnnotation}
        </FieldEscalateControl>
      </div>
    );
  }

  // Editable mode
  const sp = getFieldSuggestionProps?.(fieldPath, field.type);
  const hasSuggestionOrHint = sp?.suggestion !== undefined || sp?.hint !== undefined;
  const inputElement = renderEditableField(field, value, handleChange, handleBlur, error, domId);

  return (
    <div className={className?.container || 'px-2 py-1.5'} data-field-id={fieldPath}>
      <FieldEscalateControl fieldPath={fieldPath} fieldLabel={field.label}>
        <label
          htmlFor={domId}
          className={`${className?.label || 'text-xs text-muted-foreground mb-1'} flex items-center gap-1.5`}
        >
          {showDispositionIcon && <DispositionFieldIcon status={dispositionStatus} />}
          <span>
            {field.label}
            {field.validation?.required && <span className="text-destructive ml-1">*</span>}
          </span>
          {leafHeardSeek?.source_id && onNavigateToCall && (
            <HeardSeekButton heardSeek={leafHeardSeek} onNavigateToCall={onNavigateToCall} />
          )}
          {outputReasoning}
        </label>
        <div className="mt-1">
          {hasSuggestionOrHint && sp ? (
            <InputWithSuggestion
              suggestion={sp.suggestion}
              hint={sp.hint}
              field={field}
              value={value}
              isCollapsed={sp.isCollapsed}
              isTextarea={field.type === 'textarea'}
              onAccept={() => {
                handleChange(sp.suggestion);
                sp.onAccept();
              }}
              onReject={sp.onReject}
              onExpand={sp.onExpand}
              evidence={sp.evidence}
              sourceCallId={sp.sourceCallId}
              onNavigateToCall={sp.onNavigateToCall}
              onNavigateToAudit={sp.onNavigateToAudit}
            >
              {inputElement}
            </InputWithSuggestion>
          ) : (
            inputElement
          )}
          {error && <div className="text-xs text-destructive mt-1">{error}</div>}
          {extractedAnnotation}
        </div>
      </FieldEscalateControl>
    </div>
  );
};

/**
 * Renders the appropriate input control for a field type.
 *
 * `onBlur` is the commit signal used by the disposition assist panel.
 * For instant-commit fields (select/date/multi_select/checkbox) we fire it
 * synchronously after onChange so the panel re-evaluates immediately;
 * for text/number/textarea it's wired to the input's blur event.
 */
function renderEditableField(
  field: FormField,
  value: any,
  onChange: (value: any) => void,
  onBlur: () => void,
  error?: string,
  domId?: string,
): React.ReactNode {
  const id = domId ?? field.id;
  switch (field.type) {
    case 'select':
      if (field.options) {
        return (
          <SearchableSelect
            id={id}
            value={value ?? ''} // Use nullish coalescing to preserve false
            onValueChange={val => {
              onChange(val);
              onBlur();
            }}
            options={field.options}
            placeholder={field.placeholder || 'Select...'}
            error={!!error}
            className="h-8 text-sm"
          />
        );
      }
      return (
        <Input
          id={id}
          type="text"
          value={value ?? ''}
          onChange={e => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={field.placeholder}
          className={`h-8 text-sm w-full ${error ? 'border-destructive' : ''}`}
        />
      );

    case 'multi_select': {
      const selectedVals = Array.isArray(value)
        ? value
        : typeof value === 'string' && value
          ? [value]
          : [];
      const definedOpts =
        field.options?.map(opt => ({
          label: opt.label,
          value: String(opt.value),
        })) || [];
      // Legacy fallback: if a selected value doesn't match any defined option
      // (e.g. old free-text value before the field was changed to multi_select),
      // inject it as an ad-hoc option so it still appears checked in the dropdown.
      const definedVals = new Set(definedOpts.map(o => o.value));
      const orphanOpts = selectedVals
        .filter(v => !definedVals.has(v))
        .map(v => ({ label: v, value: v }));
      const effectiveOpts = [...definedOpts, ...orphanOpts];
      return (
        <MultiSelect
          options={effectiveOpts}
          selected={selectedVals}
          onChange={val => {
            onChange(val);
            onBlur();
          }}
          placeholder={field.placeholder || 'Select options...'}
          disabled={field.disabled}
          className={error ? 'border-destructive h-8 text-sm' : 'h-8 text-sm'}
          searchable
        />
      );
    }

    case 'date': {
      return (
        <DatePicker
          value={value}
          onChange={(date, unknown) => {
            onChange(unknown ? UNKNOWN_DATE_VALUE : date ? format(date, 'yyyy-MM-dd') : '');
            onBlur();
          }}
          placeholder={field.placeholder || 'mm/dd/yyyy'}
          disabled={false}
          allowUnknown={field.allowUnknown}
          className={`h-8 ${error ? 'border-destructive' : ''}`}
        />
      );
    }

    case 'textarea':
      return (
        <textarea
          id={id}
          value={value ?? ''}
          onChange={e => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={field.placeholder}
          disabled={field.disabled}
          className={`w-full min-h-[80px] p-2 rounded-md border text-sm focus:outline-hidden focus:ring-2 focus:ring-offset-0 ${
            error ? 'border-destructive focus:ring-destructive' : ''
          }`}
        />
      );

    case 'number':
      return (
        <Input
          id={id}
          type="number"
          value={value ?? ''}
          onChange={e => onChange(e.target.value ? Number(e.target.value) : '')}
          onBlur={onBlur}
          placeholder={field.placeholder}
          {...nonNegativeInputProps}
          className={`h-8 text-sm w-full ${error ? 'border-destructive' : ''}`}
        />
      );

    case 'float':
      return (
        <Input
          id={id}
          type="number"
          step="any"
          value={value ?? ''}
          onChange={e => onChange(e.target.value ? Number(e.target.value) : '')}
          onBlur={onBlur}
          placeholder={field.placeholder}
          {...nonNegativeInputProps}
          className={`h-8 text-sm w-full ${error ? 'border-destructive' : ''}`}
        />
      );

    case 'checkbox':
      return (
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={e => {
            onChange(e.target.checked);
            onBlur();
          }}
          className="h-4 w-4 rounded border text-primary focus:ring-primary"
        />
      );

    case 'phone':
      // Mirror the scalar phone renderer (ScalarFieldEditable) so phone fields nested
      // inside an object/array (e.g. prior_authorization.prior_authorization_phone_number)
      // get the same country-code phone input instead of falling through to a plain
      // text box (type="phone" is not a valid HTML input type).
      return (
        <ReactPhoneInput
          inputProps={{ id, className: error ? 'error' : '', onBlur }}
          country="us"
          value={value ?? ''}
          onChange={val => onChange(val)}
          disabled={field.disabled}
          inputClass="w-full!"
          containerClass="phone-input-container"
          inputStyle={{
            width: '100%',
            height: '32px',
            fontSize: '14px',
            paddingLeft: '48px',
            borderRadius: '6px',
            border: error ? '1px solid #ef4444' : '1px solid hsl(var(--border))',
            backgroundColor: 'transparent',
            outline: 'none',
            transition: 'all 0.2s',
          }}
          buttonStyle={{
            borderRadius: '6px 0 0 6px',
            border: error ? '1px solid #ef4444' : '1px solid hsl(var(--border))',
            borderRight: 'none',
            backgroundColor: 'transparent',
            transition: 'all 0.2s',
          }}
          placeholder={field.placeholder}
          enableSearch
          searchPlaceholder="Search countries..."
          dropdownStyle={{ zIndex: 99999, position: 'absolute' }}
        />
      );

    default:
      return (
        <Input
          id={id}
          type={field.type || 'text'}
          value={value ?? ''}
          onChange={e => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={field.placeholder}
          className={`h-8 text-sm w-full ${error ? 'border-destructive' : ''}`}
        />
      );
  }
}
